import { MAILCHIMP_SKU_COLUMNS } from 'config/mailchimpSkuColumns';
import { isFoodstuffsPricingType, tradePriceForSku } from 'util/mailchimpSkuPrice';

/**
 * Group non-deleted SKUs by product_matrix_id (string).
 * @param {object[]} skus
 * @returns {Map<string, object[]>}
 */
export function groupSkusByProductMatrixId(skus) {
  const m = new Map();
  if (!Array.isArray(skus)) return m;
  skus.forEach((s) => {
    if (s.deleted) return;
    const pid = String(s.product_matrix_id ?? s.productMatrixId ?? '');
    if (!pid) return;
    if (!m.has(pid)) m.set(pid, []);
    m.get(pid).push(s);
  });
  return m;
}

/** API client camelizes responses (`validityStatus`); raw rows may use `validity_status`. */
function skuValidityRank(sku) {
  return (sku.validityStatus ?? sku.validity_status) === 'valid' ? 0 : 1;
}

/** API client camelizes `isLegacy`; raw rows may use `is_legacy`. */
function skuIsLegacy(sku) {
  return Boolean(sku.isLegacy ?? sku.is_legacy);
}

/**
 * When multiple SKUs match a column: prefer valid, non-legacy, then lowest key.
 * @param {object[]} matches
 * @returns {object|null}
 */
export function pickRepresentativeSku(matches) {
  if (!matches?.length) return null;
  return [...matches].sort((a, b) => {
    const va = skuValidityRank(a);
    const vb = skuValidityRank(b);
    if (va !== vb) return va - vb;
    if (skuIsLegacy(a) !== skuIsLegacy(b)) return skuIsLegacy(a) ? 1 : -1;
    return Number(a.key) - Number(b.key);
  })[0];
}

/**
 * @param {object[]} productSkus
 * @param {{ id: string, match: (sku: object) => boolean }} column
 * @returns {object|null}
 */
export function skuForColumn(productSkus, column) {
  const matches = (productSkus || []).filter(column.match);
  return pickRepresentativeSku(matches);
}

/**
 * @param {object[]} productSkus
 * @returns {Record<string, object|null>}
 */
export function skuSlotMapForProduct(productSkus) {
  const out = {};
  MAILCHIMP_SKU_COLUMNS.forEach((col) => {
    out[col.id] = skuForColumn(productSkus, col);
  });
  return out;
}

/**
 * Parse trade price string to number for filtering (ignores currency symbols).
 * @param {string|null|undefined} raw
 * @returns {number|null}
 */
export function parseTradePriceNumber(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (s === '' || s === '-') return null;
  const n = parseFloat(s.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Product passes price range if any non-deleted SKU has trade price in [min, max] for tier.
 * Empty min/max means no bound on that side.
 * @param {object[]} productSkus
 * @param {string} pricingType
 * @param {number|null|undefined} min
 * @param {number|null|undefined} max
 */
export function productMatchesPriceRange(productSkus, pricingType, min, max) {
  if (isFoodstuffsPricingType(pricingType)) return true;
  if (min == null && max == null) return true;
  const lo = min != null && Number.isFinite(min) ? min : null;
  const hi = max != null && Number.isFinite(max) ? max : null;
  if (lo == null && hi == null) return true;

  return (productSkus || []).some((sku) => {
    const raw = tradePriceForSku(sku, pricingType);
    const n = parseTradePriceNumber(raw);
    if (n == null) return false;
    if (lo != null && n < lo) return false;
    if (hi != null && n > hi) return false;
    return true;
  });
}

/**
 * Single numeric ABV for filtering (single value, or midpoint of range).
 * @param {object} product
 * @returns {number|null}
 */
export function productAbvNumeric(product) {
  if (!product) return null;
  if (product.abv != null && product.abv !== '') {
    const n = Number(product.abv);
    return Number.isFinite(n) ? n : null;
  }
  const min = product.abvMin != null && product.abvMin !== '' ? Number(product.abvMin) : null;
  const max = product.abvMax != null && product.abvMax !== '' ? Number(product.abvMax) : null;
  if (min != null && max != null && Number.isFinite(min) && Number.isFinite(max)) {
    return (min + max) / 2;
  }
  if (min != null && Number.isFinite(min)) return min;
  if (max != null && Number.isFinite(max)) return max;
  return null;
}
